import type OpenAI from 'openai'

import type { Logger } from '../logger'
import type { AiChatBotTool, ToolResult } from '../types'

// Narrowed to function-type tool calls only (we don't handle custom tool calls)
export interface FunctionToolCall {
  id: string
  type: 'function'
  function: { name: string; arguments: string }
}

function safeParseArgs(args: string): unknown {
  try {
    return JSON.parse(args)
  } catch {
    return {}
  }
}

// ─── Mid-stream helpers ────────────────────────────────────────────────────────

export interface PartialAssistantMessage {
  role?: string
  content?: string | null
  tool_calls?: Array<{
    id: string
    type: 'function'
    function: { name: string; arguments: string }
  }>
}

export function extractPartialMessage(
  message: PartialAssistantMessage,
): OpenAI.ChatCompletionMessageParam | undefined {
  if (message.role !== 'assistant') return undefined
  return {
    role: 'assistant',
    content: message.content ?? null,
    ...(message.tool_calls?.length
      ? {
          // Cast: PartialAssistantMessage.tool_calls uses a narrower inline type — same shape as the SDK type.
          tool_calls:
            message.tool_calls as OpenAI.Chat.Completions.ChatCompletionMessageToolCall[],
        }
      : {}),
  }
}

export function isCompleteArguments(args: string | undefined): boolean {
  if (!args) return false
  try {
    JSON.parse(args)
    return true
  } catch {
    return false
  }
}

export async function runToolDirect(
  call: FunctionToolCall,
  toolMap: Map<string, AiChatBotTool<unknown>>,
  log: Logger,
): Promise<ToolResult> {
  const tool = toolMap.get(call.function.name)
  if (!tool) {
    return {
      tool_call_id: call.id,
      content: JSON.stringify({ error: `Unknown tool: ${call.function.name}` }),
    }
  }
  try {
    const args = safeParseArgs(call.function.arguments)
    const result = await tool.run(args, call.id)
    log.log(
      'Tool result:',
      call.function.name,
      '| args:',
      args,
      '| result:',
      result,
    )
    return {
      tool_call_id: call.id,
      content: typeof result === 'string' ? result : JSON.stringify(result),
    }
  } catch (error) {
    log.error('Tool error:', call.function.name, error)
    return {
      tool_call_id: call.id,
      content: JSON.stringify({ error: 'Tool execution failed.' }),
    }
  }
}
